import {
  Theme,
  propertyColorStyles,
  radius,
  rem,
  spacing,
  token,
  transition,
} from '@thiememeulenhoff/lit-sdk';
import { PropertyColor } from '@thiememeulenhoff/types';
import { css, unsafeCSS } from 'lit';
import { tagName } from '../../helpers';

export const tag = tagName('button');
export const theme = new Theme(tag);

const propertyColorCallback = (propertyColor: PropertyColor) => {
  const selector = unsafeCSS(`[color=${propertyColor}]`);
  const subtleSelector = unsafeCSS(`${selector}[variant='subtle']`);
  const borderedSelector = unsafeCSS(`${selector}[border]`);

  // postcss-lit-disable-next-line
  return css`
    :host(${selector}) :where(.button:not([disabled])) {
      ${theme.props([
        [
          'background',
          `${propertyColor}-background`,
          token(`albert-button-${propertyColor}-background-default`),
        ],
        [
          'color',
          `${propertyColor}-color`,
          token(`albert-button-${propertyColor}-foreground-inverse-default`),
        ],
      ])};

      &:hover {
        ${theme.props([
          [
            'background',
            `${propertyColor}-background-hover`,
            token(`albert-button-${propertyColor}-background-hover`),
          ],
          [
            'color',
            `${propertyColor}-color-hover`,
            token(`albert-button-${propertyColor}-foreground-inverse-hover`),
          ],
        ])};
      }
    }

    :host(${selector}[active]:not([disabled])) :where(.button),
    :host(${selector}:not([disabled])) .button:is([active], :active) {
      ${theme.props([
        [
          'background',
          `${propertyColor}-background-active`,
          token(`albert-button-${propertyColor}-background-pressed`),
        ],
        [
          'color',
          `${propertyColor}-color-active`,
          token(`albert-button-${propertyColor}-foreground-inverse-pressed`),
        ],
      ])};
    }

    :host(${selector}) :where(.button):focus-visible {
      outline-color: var(--tm-focus-ring-color);
      outline-offset: var(--tm-focus-ring-offset);
      outline-style: var(--tm-focus-ring-style);
      outline-width: var(--tm-focus-ring-width);
    }

    :host(${borderedSelector}) :where(.button:not([disabled])) {
      outline: ${rem(1)} solid
        ${token(`albert-button-${propertyColor}-border-default`)};
    }

    :host(${selector}[variant='ghost']) .button:not(:is(:hover, :active, [active], [disabled])) {
      background: transparent;
      color: ${token(`albert-button-${propertyColor}-foreground-default`)};
    }

    :host(${selector}[variant='ghost']) .button:hover:not([disabled]) {
      background: ${
        propertyColor === 'beige'
          ? token(`albert-button-${propertyColor}-ghost-background-hover`)
          : unsafeCSS('transparent')
      };
      color: ${token(`albert-button-${propertyColor}-foreground-hover`)};
    }

    :host(${selector}[variant='subtle']) .button:not(:is(:hover, :active, [active], [disabled])) {
      background: ${token(`albert-button-${propertyColor}-subtle-background-default`)};
      color: ${token(`albert-button-${propertyColor}-foreground-default`)};
    }

    :host(${subtleSelector}) .button:hover:not([disabled]) {
      background: ${token(`albert-button-${propertyColor}-subtle-background-hover`)};
      color: ${token(`albert-button-${propertyColor}-foreground-hover`)};
    }

    :host(${selector}:not([disabled])[active][variant='ghost']) .button {
      background: ${token(`albert-button-${propertyColor}-ghost-background-pressed`)};
      color: ${token(`albert-button-${propertyColor}-foreground-inverse-pressed`)};
    }

    &:host(${selector}[variant='ghost']:not([disabled])) .button:active {
      background: ${token(`albert-button-${propertyColor}-ghost-background-pressed`)};
      color: ${token(`albert-button-${propertyColor}-foreground-inverse-pressed`)};
    }

    :host(${selector}[variant='text']) .button {
      background: transparent;
      padding: 0;
    }

    :host(${selector}[variant='text']) .button:not(:is(:hover, :active, [active], [disabled])) {
      color: ${token(`albert-button-${propertyColor}-foreground-default`)};
    }

    :host(${selector}[variant='text']) .button:hover {
      color: ${token(`albert-button-${propertyColor}-foreground-hover`)};
    }

    :host(${selector}[variant='text']) .button:is(:disabled, [disabled]) {
      color: ${token('foreground-disabled')};
    }

    :host(${selector}[variant='text']:not([disabled])) .button:is(:active, [active]),
    :host(${selector}[variant='text']:not([disabled]):is([active], :active)) .button {
      color: ${token(`albert-button-${propertyColor}-foreground-pressed`)};
    }

    :host(${subtleSelector}[active]) .button {
      background: ${token(`albert-button-${propertyColor}-subtle-background-pressed`)};
      color: ${token(`albert-button-${propertyColor}-foreground-inverse-pressed`)};
    }

    :host(${subtleSelector}) .button:active:not([disabled]) {
      background: ${token(`albert-button-${propertyColor}-subtle-background-pressed`)};
      color: ${token(`albert-button-${propertyColor}-foreground-inverse-pressed`)};
    }

    :host(${selector}) :where(.button[disabled]) {
      background: ${token('background-disabled')};
      color: ${token('foreground-disabled')};
      cursor: not-allowed;
    }
  `;
};

const styles = [];

styles.push(css`
  :host {
    display: inline-block;
  }

  :where(.button) {
    align-items: center;
    box-sizing: border-box;
    cursor: pointer;
    display: inline-flex;
    user-select: none;

    ${theme.props([
      ['block-size', 'block-size', rem(40)],
      ['inline-size', 'inline-size', '100%'],
      ['padding', 'padding', spacing(0, 200)],
      ['border', 'border', '0'],
      ['border-radius', 'border-radius', radius('large')],
      ['color', 'color', token('foreground-primary')],
      ['font-size', 'font-size', rem(15)],
      ['font-family', 'font-family', 'inherit'],
      ['font-weight', 'font-weight', '600'],
      ['gap', 'gap', spacing(100)],
      ['line-height', 'line-height', '1'],
      ['text-align', 'text-align', 'center'],
      ['text-decoration', 'text-decoration', 'none'],
      [
        'transition',
        'transition',
        'color var(--tm-transition-duration-fast) var(--tm-transition-function-ease-out), background var(--tm-transition-duration-fast) var(--tm-transition-function-ease-out)',
      ],
      ['white-space', 'white-space', 'nowrap'],
    ])};
  }

  :host([size='small']) :where(.button) {
    ${theme.props([
      ['padding', 'padding', spacing(75, 150)],
      ['font-size', 'font-size', rem(14)],
      ['block-size', 'block-size', rem(32)],
    ])};
  }

  :host([pill]) :where(.button) {
    border-radius: ${radius('extra-large')};
  }

  :where(.button:not([disabled])) {
    transition: ${transition('background-color', 'normal', 'ease-out')};
  }

  :where(.button:not([disabled])):hover {
    transition: ${transition('background-color', 'normal', 'ease-in')};
  }

  :host([animation='false']) :where(.button:not([disabled])),
  :host([animation='false']) :where(.button:not([disabled])):hover {
    transition: none;
  }

  :host(:where([variant='default'], [variant='subtle'])) :where(.button) {
    ${theme.props([
      ['box-shadow', 'box-shadow', token('elevation-shadow-raised')],
    ])};
  }
`);

styles.push(propertyColorStyles(propertyColorCallback));

export const buttonStyles = styles;
